/**
 * AANKH - Root Application Component
 * 
 * @author Harshit Khemani
 * @version 1.9.0
 * @description Main application layout with device provider and panels
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DeviceProvider, useDevice } from './hooks/useDevice';
import Header from './components/Header';
import ConnectionScreen from './components/ConnectionScreen';
import CameraPanel from './components/CameraPanel';
import MicrophonePanel from './components/MicrophonePanel';
import SettingsPanel from './components/SettingsPanel';
import aankhLogo from '../assets/AANKH/AANKH-wordmark.svg';
import brilliantSoleLogo from '../assets/BrilliantSole.png';
import omiLogo from '../assets/Omi.png';

/**
 * Main App Content - Uses device context
 */
function AppContent() {
  const { isConnected } = useDevice();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header with connection controls */}
      <Header onOpenSettings={() => setIsSettingsOpen(true)} />
      
      {/* Settings Panel Modal */}
      <SettingsPanel 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />
      
      {/* Main Content */}
      <main className="flex-1 w-full">
        <AnimatePresence mode="wait">
          {!isConnected ? (
            // Connection Screen
            <motion.div
              key="connection"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="min-h-[calc(100vh-4rem)] flex items-center"
            >
              <ConnectionScreen />
            </motion.div>
          ) : (
            // Connected State - Show Panels
            <motion.div
              key="connected"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12"
            >
              {/* Feature Panels */}
              <div className="space-y-6 sm:space-y-8 lg:space-y-10">
                {/* Camera and Microphone - Responsive grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <CameraPanel />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <MicrophonePanel />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t border-gray-200 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-12 sm:py-16">
          {/* Main Footer Content */}
          <div className="space-y-12">
            {/* Top Section: Logo and Creator */}
            <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8">
              {/* AANKH Brand */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex flex-col items-center md:items-start gap-3"
              >
                <motion.img
                  src={aankhLogo}
                  alt="AANKH"
                  className="h-8 sm:h-9"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                />
                <p className="text-sm text-gray-600 text-center md:text-left">
                  Created by{' '}
                  <a
                    href="https://github.com/HKTITAN"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold hover:underline inline-flex items-center gap-1"
                  >
                    Harshit Khemani
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </p>
                <p className="text-xs text-gray-500 font-mono">v1.4.0 • MIT License</p>
              </motion.div>

              {/* Built With Section */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col gap-6"
              >
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center md:text-left">
                  Built With
                </h4>
                <div className="flex flex-col sm:flex-row gap-6 items-center md:items-start">
                  {/* BrilliantSole */}
                  <motion.a
                    href="https://brilliantsole.github.io/BrilliantSole-JavaScript-SDK/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-3 group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="rounded-xl bg-white p-4 border border-gray-200 shadow-sm
                      group-hover:shadow-md group-hover:border-gray-300 transition-all">
                      <img 
                        src={brilliantSoleLogo} 
                        alt="BrilliantSole SDK" 
                        className="h-10 sm:h-12 w-auto object-contain"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                        BrilliantSole SDK
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">by Zack Qattan</p>
                    </div>
                  </motion.a>

                  {/* Omi Glass */}
                  <motion.a
                    href="https://omi.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-3 group"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <div className="rounded-xl bg-white p-4 border border-gray-200 shadow-sm
                      group-hover:shadow-md group-hover:border-gray-300 transition-all">
                      <img 
                        src={omiLogo} 
                        alt="Omi Glass" 
                        className="h-10 sm:h-12 w-auto object-contain"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                        Omi Glass
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">by Based Hardware</p>
                    </div>
                  </motion.a>
                </div>
              </motion.div>
            </div>

            {/* Bottom Section: Copyright */}
            <div className="pt-8 border-t border-gray-200">
              <p className="text-center text-xs text-gray-500">
                © {new Date().getFullYear()} AANKH by Harshit Khemani (@HKTITAN)
              </p>
              <p className="text-center text-xs text-gray-400 mt-1">
                Powered by BrilliantSole SDK by Zack Qattan • Omi Glass by Based Hardware
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * App Root - Wraps content with providers
 */
function App() {
  return (
    <DeviceProvider>
      <AppContent />
    </DeviceProvider>
  );
}

export default App;
